/**
 * Template utilities — handles bundling plans into library templates
 * and importing templates into company-specific plans.
 *
 * Two main flows:
 * 1. saveAsTemplate: TrainingPlan + Topics → LibraryPlanTemplate + LibraryTopicTemplates
 * 2. importTemplate: LibraryPlanTemplate → TrainingPlan + TrainingTopics + TrainingSchedule (new company)
 */

import {
    createLibraryTopicTemplate,
    createLibraryPlanTemplate,
    updateLibraryPlanTemplate,
    listAllLibraryPlanTemplates,
    listAllLibraryTopicTemplates,
} from '../graphql/resourceLibraryQueries';
import {
    createTrainingTopic,
    createTrainingPlan,
    createTrainingSchedule,
    updateTrainingTopic,
} from '../graphql/trainingQueries';
import type {
    TrainingTopic,
    TrainingPlan,
    TrainingSchedule,
    PlanCategory,
} from '../types/trainingTypes';
import type {
    LibraryPlanCategory,
    LibraryCategoryTopicRef,
} from '../types/resourceLibraryTypes';

// ---------------------------------------------------------------------------
// Save as Template
// ---------------------------------------------------------------------------

export interface SaveAsTemplateInput {
    plan: TrainingPlan;
    topics: TrainingTopic[];
    schedules: TrainingSchedule[];
    adminId: string;
    adminName: string;
}

export interface SaveAsTemplateResult {
    templateId: string;
    topicTemplateCount: number;
}

/**
 * Bundles a TrainingPlan and its associated topics/schedule into
 * LibraryTopicTemplates + a LibraryPlanTemplate.
 *
 * - Creates LibraryTopicTemplate for each unique topic in the plan
 * - Builds LibraryPlanCategory[] with topicRefs pointing to those templates
 * - Creates or updates the LibraryPlanTemplate
 */
export async function saveAsTemplate(input: SaveAsTemplateInput): Promise<SaveAsTemplateResult> {
    const { plan, topics, schedules, adminId, adminName } = input;

    // Parse plan categories
    let categories: PlanCategory[] = [];
    try { categories = JSON.parse(plan.categories) as PlanCategory[]; } catch { /* empty */ }

    // Get topics used in this plan — from schedule entries AND planIds
    const scheduleTopicIds = new Set(schedules.filter(s => s.planId === plan.id).map(s => s.topicId));
    const planTopics = topics.filter(t =>
        scheduleTopicIds.has(t.id) || t.planIds?.includes(plan.id)
    );

    // Create LibraryTopicTemplate for each topic (or find existing by topicId)
    const existingLibraryTopics = await listAllLibraryTopicTemplates();
    const topicIdToTemplateId = new Map<string, string>();

    for (const topic of planTopics) {
        // Check if this topic already exists in the library (by topicId match)
        const existing = existingLibraryTopics.find(
            (lt: any) => lt.topicId === topic.topicId
        );

        if (existing) {
            topicIdToTemplateId.set(topic.id, existing.id);
        } else {
            const created = await createLibraryTopicTemplate({
                topicId: topic.topicId,
                description: topic.description,
                style: topic.style,
                timeValue: topic.timeValue,
                contentFilePath: topic.contentFilePath || null,
                contentUrl: topic.contentUrl || null,
                createdByAdminId: adminId,
            } as any);
            if (!created) throw new Error('Failed to create library topic template.');
            topicIdToTemplateId.set(topic.id, created.id);
        }
    }

    // Build the schedule order: for each category, get topics in dayNumber order
    const planSchedules = schedules
        .filter(s => s.planId === plan.id)
        .sort((a, b) => a.dayNumber - b.dayNumber);

    // Build LibraryPlanCategory[] with topicRefs
    const libraryCategories: LibraryPlanCategory[] = categories.map(cat => {
        // Get schedule entries for this category, in order
        const catSchedules = planSchedules.filter(s => s.categoryId === cat.id);

        const topicRefs: LibraryCategoryTopicRef[] = catSchedules
            .map((sched, idx) => {
                const topic = planTopics.find(t => t.id === sched.topicId);
                const templateId = topicIdToTemplateId.get(sched.topicId);
                if (!topic || !templateId) return null;
                return {
                    libraryTopicTemplateId: templateId,
                    topicId: topic.topicId,
                    description: topic.description,
                    style: topic.style,
                    timeValue: topic.timeValue,
                    sortOrder: idx + 1,
                };
            })
            .filter(Boolean) as LibraryCategoryTopicRef[];

        return {
            id: cat.id,
            name: cat.name,
            targetMinutes: cat.targetMinutes,
            topicRefs,
        };
    });

    // Check if a template with this plan name already exists
    const existingTemplates = await listAllLibraryPlanTemplates();
    const existingMatch = existingTemplates.find((t: any) => t.name === plan.name);

    const templateInput = {
        name: plan.name,
        governingStandard: plan.governingStandard,
        minimumTimeRequired: plan.minimumTimeRequired,
        cycleDuration: plan.cycleDuration,
        categories: JSON.stringify(libraryCategories),
        workingDays: plan.workingDays || [1, 2, 3, 4, 5],
        status: 'ACTIVE',
        createdByAdminId: adminId,
        createdByAdminName: adminName,
        topicCount: planTopics.length,
        categoryCount: categories.length,
    };

    let templateId: string;
    if (existingMatch) {
        await updateLibraryPlanTemplate({ id: existingMatch.id, ...templateInput });
        templateId = existingMatch.id;
    } else {
        const created = await createLibraryPlanTemplate(templateInput as any);
        if (!created) throw new Error('Failed to create library plan template.');
        templateId = created.id;
    }

    return {
        templateId,
        topicTemplateCount: topicIdToTemplateId.size,
    };
}

// ---------------------------------------------------------------------------
// Import Template into Company
// ---------------------------------------------------------------------------

export interface ImportTemplateInput {
    templateId: string;
    companyId: string;
}

export interface ImportTemplateResult {
    planId: string;
    topicCount: number;
    scheduleCount: number;
}

/**
 * Imports a LibraryPlanTemplate into a company as a fully independent
 * TrainingPlan with its own TrainingTopics and TrainingSchedule entries.
 *
 * - Creates new TrainingTopic records under the company's companyId
 * - Creates a new TrainingPlan (DRAFT status, no startDate)
 * - Creates TrainingSchedule entries based on topicRefs order (dayNumber)
 * - S3 content files are NOT copied — they remain at their original public paths
 *   which are readable by all authenticated users
 */
export async function importTemplateToCompany(input: ImportTemplateInput): Promise<ImportTemplateResult> {
    const { templateId, companyId } = input;

    // Fetch the template
    const allTemplates = await listAllLibraryPlanTemplates();
    const template = allTemplates.find((t: any) => t.id === templateId);
    if (!template) throw new Error(`Template ${templateId} not found`);

    // Parse library categories
    let libCategories: LibraryPlanCategory[] = [];
    try { libCategories = JSON.parse(template.categories as string) as LibraryPlanCategory[]; } catch { /* empty */ }

    // Fetch library topic templates referenced by this plan
    const allLibTopics = await listAllLibraryTopicTemplates();
    const libTopicMap = new Map<string, any>();
    allLibTopics.forEach((lt: any) => libTopicMap.set(lt.id, lt));

    // Generate new category IDs for the company plan
    const oldCatIdToNew = new Map<string, string>();
    const newCategories: PlanCategory[] = libCategories.map(cat => {
        const newId = crypto.randomUUID();
        oldCatIdToNew.set(cat.id, newId);
        return {
            id: newId,
            name: cat.name,
            targetMinutes: cat.targetMinutes,
        };
    });

    // Create topics — deduplicate by libraryTopicTemplateId
    const libTopicIdToNewTopicId = new Map<string, string>();
    let topicCount = 0;

    for (const cat of libCategories) {
        for (const ref of (cat.topicRefs ?? [])) {
            if (libTopicIdToNewTopicId.has(ref.libraryTopicTemplateId)) continue;

            const libTopic = libTopicMap.get(ref.libraryTopicTemplateId);
            const topicData = libTopic || ref; // Fallback to ref data if topic template not found

            const created = await createTrainingTopic({
                companyId,
                topicId: topicData.topicId || ref.topicId,
                description: topicData.description || ref.description,
                style: topicData.style || ref.style,
                timeValue: topicData.timeValue || ref.timeValue,
                contentFilePath: topicData.contentFilePath || null,
                contentUrl: topicData.contentUrl || null,
                categoryIds: [oldCatIdToNew.get(cat.id)!],
                planIds: [],
                sourceLibraryTopicId: ref.libraryTopicTemplateId,
            } as any);
            if (!created) throw new Error('Failed to create training topic from template.');

            libTopicIdToNewTopicId.set(ref.libraryTopicTemplateId, created.id);
            topicCount++;
        }
    }

    // Create the plan
    const plan = await createTrainingPlan({
        companyId,
        name: template.name,
        governingStandard: template.governingStandard,
        minimumTimeRequired: template.minimumTimeRequired,
        cycleDuration: template.cycleDuration,
        status: 'DRAFT',
        categories: JSON.stringify(newCategories),
        workingDays: template.workingDays || [1, 2, 3, 4, 5],
        sourceTemplateId: templateId,
    } as any);
    if (!plan) throw new Error('Failed to create training plan from template.');

    // Create schedule entries based on topicRefs order
    let dayNumber = 0;
    let scheduleCount = 0;

    for (const cat of libCategories) {
        const newCatId = oldCatIdToNew.get(cat.id)!;
        for (const ref of (cat.topicRefs ?? []).sort((a, b) => a.sortOrder - b.sortOrder)) {
            const newTopicId = libTopicIdToNewTopicId.get(ref.libraryTopicTemplateId);
            if (!newTopicId) continue;

            dayNumber++;
            // Use a placeholder date — will be recomputed when start date is set
            const placeholderDate = `0001-01-${String(dayNumber).padStart(2, '0')}`;

            await createTrainingSchedule({
                companyId,
                planId: plan.id,
                topicId: newTopicId,
                scheduledDate: placeholderDate,
                dayNumber,
                categoryId: newCatId,
                locked: false,
            } as any);
            scheduleCount++;
        }
    }

    // Update topics with the new planId so they show in plan-filtered topic lists
    const allNewTopicIds = Array.from(libTopicIdToNewTopicId.values());
    for (const newTopicId of allNewTopicIds) {
        await updateTrainingTopic({
            id: newTopicId,
            planIds: [plan.id],
        } as any);
    }

    return {
        planId: plan.id,
        topicCount,
        scheduleCount,
    };
}
